import React, { useState } from 'react';
import { Box, Button, Text } from '@rsos/base-components';
import InputText from '@rsos/components/capstone/base/form/inputs/InputText';
import { DebugPanel } from '../DebugPanel';
import { ESRIMapProps, GoogleMapProps } from '../types';
import useLocationPicker from './useLocationPicker';
import usePlotLocations from './usePlotLocations';

type MapProps = ESRIMapProps | GoogleMapProps | undefined;

interface LocationDebugPanelControlsProps {
  phoneNumber: string;
}

const LocationDebugPanelControls: React.FC<LocationDebugPanelControlsProps> = ({
  phoneNumber,
}) => {
  // Can't find any other way of accessing window.__RSOS without crashing
  // the authentication micro app
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const map = (window as any).__RSOS?.map as MapProps;
  const [includeSecondaryLocation, setIncludeSecondaryLocation] =
    useState(false);
  const [sendSecondaryLocationFirst, setSendSecondaryLocationFirst] =
    useState(false);
  const location = useLocationPicker(map);
  const {
    startPlotting,
    pausePlotting,
    plotNextLocation,
    isPlotting,
    currentLocation,
  } = usePlotLocations({
    phoneNumber,
    includeSecondaryLocation,
    sendSecondaryLocationFirst,
    location,
  });

  if (location) {
    return (
      <Box>
        <Text size="small" color="primaryText">
          <strong>Latitude: </strong> {currentLocation?.latitude}
        </Text>
        <Text size="small" color="primaryText">
          <strong>Longitude: </strong> {currentLocation?.longitude}
        </Text>
        <Text size="normal" color="primaryText">
          <label>
            <input
              type="checkbox"
              checked={includeSecondaryLocation}
              onChange={() =>
                setIncludeSecondaryLocation(!includeSecondaryLocation)
              }
            />
            Use Secondary Location
          </label>
        </Text>
        <Text size="normal" color="primaryText">
          <label>
            <input
              type="checkbox"
              checked={sendSecondaryLocationFirst}
              disabled={!includeSecondaryLocation}
              onChange={() =>
                setSendSecondaryLocationFirst(!sendSecondaryLocationFirst)
              }
            />
            Send Secondary Location First
          </label>
        </Text>
        <Box>
          <Button
            onClick={plotNextLocation}
            title="Plot next location"
            btnType="primary"
          >
            <strong>Plot Next Location</strong>
          </Button>
          {!isPlotting ? (
            <Button onClick={startPlotting} title="Play" btnType="secondary">
              <strong>Play</strong>
            </Button>
          ) : (
            <Button onClick={pausePlotting} title="Pause" btnType="secondary">
              <strong>Pause</strong>
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Text>
      <strong>Click the map.</strong>
    </Text>
  );
};

interface LocationDebugPanelProps {
  onClose?: () => void;
}

const LocationDebugPanel: React.FC<LocationDebugPanelProps> = ({ onClose }) => {
  const [phoneNumber, setPhoneNumber] = useState('15555555555');

  return (
    <DebugPanel title="Plot Call Locations" onClose={onClose}>
      <Text size="small" color="primaryText">
        Click anywhere inside the jurisdiction on the map to select the starting
        location or the next location to plot. Then click either "Play" to
        continuously plot locations, or click "Plot Next Location" to plot a
        single location.
      </Text>
      <Text size="normal" color="primaryText">
        <label>
          <InputText
            type="text"
            onChange={event => setPhoneNumber(event.target.value)}
            value={phoneNumber}
          />
        </label>
      </Text>
      <LocationDebugPanelControls phoneNumber={phoneNumber} />
    </DebugPanel>
  );
};

export default LocationDebugPanel;
